import { plateFont } from '../../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Slide,
  Collapse,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as AssignmentIcon,
  InfoOutlined as InfoOutlinedIcon,
  Business as BusinessIcon,
  PersonSearch as PersonSearchIcon,
  StickyNote2 as StickyNoteIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import { can } from '../../../utils/access';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, setTimeOfDay, workScheduleHoursForDate } from '../hooks/dateUtils';
import { DURATION_OPTIONS } from '../calendarConstants';
import useCalendarColors from '../hooks/useCalendarColors';
import useCalendarState from '../hooks/useCalendarState';
import CustomerVehicleChips from '../../shared/CustomerVehicleChips';
import ScheduleReminderSection, { useScheduleReminders } from '../../shared/ScheduleReminderSection';
import BookingNotifyChoice from '../../shared/BookingNotifyChoice';
import { isFinnishRegistrationNumber } from '../../../utils/registration';
import NewCustomerForm, {
  EMPTY_NEW_CUSTOMER,
  isNewCustomerValid,
  isNewCustomerIncomplete,
} from '../../shared/NewCustomerForm';
import { customerLabel } from '../../../utils/customer';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// The "something else than a booking" buttons at the top of the create dialog
const ALT_ACTION_SX = {
  justifyContent: 'flex-start',
  textTransform: 'none',
  borderRadius: 2,
  borderColor: 'divider',
  color: 'text.primary',
  bgcolor: 'background.paper',
};

function MaintenanceSearchAutocomplete({ t, value, onChange }) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!inputValue || inputValue.length < 1) {
      if (!inputValue) {
        setLoading(true);
        axios.get('/api/scheduling/calendar/schedulable/').then((res) => {
          setOptions(res.data);
        }).finally(() => setLoading(false));
      }
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      axios.get('/api/scheduling/calendar/schedulable/', {
        params: { search: inputValue },
      }).then((res) => {
        setOptions(res.data);
      }).finally(() => setLoading(false));
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue]);

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
      onChange={(_, newOption) => {
        setSelectedOption(newOption);
        onChange(newOption ? newOption.id : '');
      }}
      getOptionLabel={(option) =>
        `${option.vehicle_registration || ''} ${option.customer_name || ''} #${option.work_number || ''}`.trim()
      }
      isOptionEqualToValue={(option, val) => option.id === val.id}
      noOptionsText={t('No results')}
      loadingText={t('Loading...')}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {option.vehicle_registration}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.customer_name}{option.description ? ` - ${option.description}` : ''}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete="off"
          placeholder={t('Search maintenance...')}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
        />
      )}
      fullWidth
      size="small"
    />
  );
}

function MobileScheduleDialog({
  t,
  open,
  mode, // 'create' or 'edit'
  selectedTimeSlot,
  selectedEvent,
  onClose,
  onSuccess,
  // Opens the note dialog for the tapped slot instead; absent where notes do not belong (a bay)
  onAddNote,
  // Opens the absence dialog for the tapped slot; only given for a mechanic's column
  onAddUnavailability,
  employees,
  serviceBays,
  workSchedule,
}) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  const canTag = useUserStore((s) => can(s.currentOrganization, 'work_orders.edit'));
  const { resolveEvent, activeTags } = useCalendarColors();
  const updateEventsByMaintenance = useCalendarState((s) => s.updateEventsByMaintenance);
  const updateUnscheduledMaintenance = useCalendarState((s) => s.updateUnscheduledMaintenance);
  const navigate = useNavigate();
  const isEditMode = mode === 'edit' && selectedEvent;

  // Touch has no right-click, so the colour tag is picked here. Kept in local state so the
  // summary card repaints at once; the calendar store is patched for the cards behind it.
  const [colorTagId, setColorTagId] = useState(selectedEvent?.color_tag_id ?? null);
  useEffect(() => {
    setColorTagId(selectedEvent?.color_tag_id ?? null);
  }, [selectedEvent]);

  // Booking-linked work orders: tell the customer about the moved time? Reset on every open.
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  useEffect(() => {
    if (open) setNotifyCustomer(true);
  }, [open, selectedEvent]);

  const handleColorTagChange = async (value) => {
    const nextId = value === '' ? null : value;
    const previous = colorTagId;
    setColorTagId(nextId);
    try {
      await axios.post(`/api/maintenances/${selectedEvent.maintenance_id}/set-color-tag/`, {
        color_tag: nextId,
      });
      updateEventsByMaintenance(selectedEvent.maintenance_id, { color_tag_id: nextId });
      updateUnscheduledMaintenance(selectedEvent.maintenance_id, { color_tag_id: nextId });
    } catch (err) {
      setColorTagId(previous);
      setError(t('calendar.contextMenu.colorTagFailed'));
    }
  };

  
  // Default start without a clicked slot: today's work day start (the week's bounds on a closed day)
  const startHourInt = workScheduleHoursForDate(workSchedule, new Date()).startHour;

  const [formData, setFormData] = useState({
    maintenanceId: '',
    startDateTime: null,
    endDateTime: null,
    serviceBayId: '',
    employeeIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);
  // The error alert sits at the top of the scrolling content, the save button at the bottom
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // New state for direct maintenance creation
  const [creationMode, setCreationMode] = useState('existing'); // 'existing' or 'new'
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);
  const [newMaintenanceData, setNewMaintenanceData] = useState({
    registrationNumber: '',
    description: '',
    vehicle: null,
    customerId: '',
  });
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [vehicleLookedUp, setVehicleLookedUp] = useState(false);
  const [showTrialInfo, setShowTrialInfo] = useState(false);
  const lookupTimeoutRef = useRef(null);
  const customerAutoFilledRef = useRef('none'); // 'none' | 'auto' | 'manual'
  const [vehicleTypeaheadOptions, setVehicleTypeaheadOptions] = useState([]);
  const [vehicleTypeaheadLoading, setVehicleTypeaheadLoading] = useState(false);
  const vehicleTypeaheadTimerRef = useRef(null);
  const vehicleTypeaheadOpenRef = useRef(false);

  useEffect(() => {
    if (vehicleLookedUp) {
      setVehicleTypeaheadOptions([]);
    }
  }, [vehicleLookedUp]);

  // Customer creation mode
  const [customerMode, setCustomerMode] = useState('existing'); // 'existing' or 'new'
  const [newCustomer, setNewCustomer] = useState({ ...EMPTY_NEW_CUSTOMER });

  // Customer reminder / notification state (shared section + save helper).
  const reminderMaintenanceId = isEditMode
    ? selectedEvent?.maintenance_id
    : creationMode === 'existing'
      ? formData.maintenanceId || null
      : null;
  const reminders = useScheduleReminders({
    open,
    maintenanceId: reminderMaintenanceId,
    scheduleId: isEditMode ? selectedEvent?.id : null,
  });

  // Fetch customers when dialog opens or when search term changes
  useEffect(() => {
    if (open) {
      const fetchCustomers = async () => {
        setCustomerLoading(true);
        try {
          const params = customerSearchTerm
            ? { search: customerSearchTerm, typeahead: 'true' }
            : { page_size: 50 };
          const res = await axios.get('/api/customers/', { params });
          // Handle paginated response
          const customersData = res.data.results || res.data;
          setCustomers(Array.isArray(customersData) ? customersData : []);
        } catch (err) {
          console.error('Error fetching customers:', err);
        } finally {
          setCustomerLoading(false);
        }
      };

      const debounceTimer = setTimeout(fetchCustomers, customerSearchTerm ? 300 : 0);
      return () => clearTimeout(debounceTimer);
    }
  }, [open, customerSearchTerm]);

  // Search for existing vehicle in organization database (free, no quota impact)
  const searchExistingVehicle = useCallback(async (regNumber) => {
    if (!regNumber || regNumber.length < 3) return null;
    try {
      const response = await axios.get(`/api/vehicles/by-registration/?registration_number=${encodeURIComponent(regNumber)}`);
      if (response.data.found && response.data.vehicle) {
        return { vehicle: response.data.vehicle, lastCustomer: response.data.last_customer || null };
      }
      return null;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') return null;
      console.error('Vehicle search failed:', error);
      return null;
    }
  }, []);

  // Prefill customer from vehicle lookup result
  const prefillCustomer = useCallback((customerData) => {
    if (customerAutoFilledRef.current === 'manual') return; // Don't override manual selection
    setNewMaintenanceData((prev) => ({ ...prev, customerId: customerData.id }));
    customerAutoFilledRef.current = 'auto';
    setCustomers((prev) => {
      if (prev.some((c) => c.id === customerData.id)) return prev;
      return [customerData, ...prev];
    });
  }, []);

  // Vehicle lookup function with debounce - backend handles trial check
  const lookupVehicle = useCallback(async (regNumber) => {
    if (!regNumber) return;

    setLookupLoading(true);
    setLookupError(null);
    setShowTrialInfo(false);

    try {
      // First check if vehicle exists in org database (free)
      const existing = await searchExistingVehicle(regNumber);
      if (existing) {
        setNewMaintenanceData((prev) => ({
          ...prev,
          registrationNumber: regNumber.toUpperCase(),
          vehicle: existing.vehicle,
        }));
        if (existing.lastCustomer) {
          prefillCustomer(existing.lastCustomer);
        }
        const hasData = existing.vehicle.manufacturer || existing.vehicle.model || existing.vehicle.vin;
        if (hasData) {
          setVehicleLookedUp(true);
          setLookupLoading(false);
          return;
        }
      }

      // Not Finnish pattern - skip external lookup
      if (!isFinnishRegistrationNumber(regNumber)) {
        setLookupLoading(false);
        return;
      }

      // Finnish pattern - try external lookup
      const response = await axios.post('/api/vehicles/lookup/', {
        registration_number: regNumber.toUpperCase(),
      });

      // Handle trial restriction from backend (fallback)
      if (response.data.trial_restricted) {
        setShowTrialInfo(true);
        setLookupLoading(false);
        return;
      }

      if (response.data.success && response.data.vehicle) {
        const vehicleData = response.data.vehicle;
        setNewMaintenanceData((prev) => ({
          ...prev,
          registrationNumber: regNumber.toUpperCase(),
          vehicle: {
            registration_number: regNumber.toUpperCase(),
            manufacturer: vehicleData.manufacturer || '',
            model: vehicleData.model || '',
            year_model: vehicleData.year_model || vehicleData.model_year_from || '',
            fuel_type: vehicleData.fuel_type || '',
            engine_kw: vehicleData.engine_kw || '',
          },
        }));
        setVehicleLookedUp(true);
      } else {
        setLookupError(t('Vehicle not found'));
      }
    } catch (error) {
      console.error('Vehicle lookup error:', error);
      setLookupError(t('Vehicle lookup failed'));
    } finally {
      setLookupLoading(false);
    }
  }, [t, searchExistingVehicle, prefillCustomer]);

  // Handle registration number change with debounced lookup
  const handleRegistrationChange = (value) => {
    const upperValue = value.toUpperCase();
    // Check and mutate ref synchronously OUTSIDE state updater to avoid
    // React StrictMode double-invocation issues.
    const shouldClearCustomer = customerAutoFilledRef.current === 'auto';
    if (shouldClearCustomer) {
      customerAutoFilledRef.current = 'none';
    }
    setNewMaintenanceData((prev) => {
      const updates = { ...prev, registrationNumber: upperValue, vehicle: null };
      if (shouldClearCustomer) {
        updates.customerId = '';
      }
      return updates;
    });
    setVehicleLookedUp(false);
    setLookupError(null);
    setShowTrialInfo(false);

    // Clear existing timeout
    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }

    // Set new timeout for lookup (search existing vehicles for any pattern >= 3 chars)
    if (upperValue.length >= 3) {
      lookupTimeoutRef.current = setTimeout(() => {
        lookupVehicle(upperValue);
      }, 800);
    }

    // Typeahead search
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    if (upperValue.length >= 2) {
      vehicleTypeaheadTimerRef.current = setTimeout(() => {
        searchVehicleTypeahead(upperValue);
      }, 500);
    } else {
      setVehicleTypeaheadOptions([]);
    }
  };

  const searchVehicleTypeahead = async (query) => {
    if (!query || query.length < 2) {
      setVehicleTypeaheadOptions([]);
      return;
    }
    setVehicleTypeaheadLoading(true);
    try {
      const response = await axios.get(`/api/vehicles/typeahead/?search=${encodeURIComponent(query)}`);
      setVehicleTypeaheadOptions(response.data || []);
    } catch (err) {
      setVehicleTypeaheadOptions([]);
    } finally {
      setVehicleTypeaheadLoading(false);
    }
  };

  const handleVehicleTypeaheadSelect = (option) => {
    setVehicleTypeaheadOptions([]);
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }
    setNewMaintenanceData((prev) => ({ ...prev, registrationNumber: option.registration_number, vehicle: null }));
    lookupVehicle(option.registration_number);
  };


  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setDeleteConfirm(false);
      // Reset creation mode state
      setCreationMode('existing');
      setNewMaintenanceData({
        registrationNumber: '',
        description: '',
        vehicle: null,
        customerId: '',
      });
      setVehicleLookedUp(false);
      setLookupError(null);
      setShowTrialInfo(false);
      customerAutoFilledRef.current = 'none';
      if (vehicleTypeaheadTimerRef.current) {
        clearTimeout(vehicleTypeaheadTimerRef.current);
      }
      setVehicleTypeaheadOptions([]);
      setCustomerMode('existing');
      setNewCustomer({ ...EMPTY_NEW_CUSTOMER });

      if (isEditMode && selectedEvent) {
        // Edit mode - pre-fill with existing event data
        setFormData({
          maintenanceId: selectedEvent.maintenance_id,
          startDateTime: new Date(selectedEvent.start_time),
          endDateTime: new Date(selectedEvent.end_time),
          serviceBayId: selectedEvent.service_bay_id || '',
          employeeIds: selectedEvent.employee_ids || [],
        });
      } else if (selectedTimeSlot) {
        // Create mode - use selected time slot from calendar
        const startDate = selectedTimeSlot.startDate || selectedTimeSlot.date;
        const endDate = selectedTimeSlot.endDate || selectedTimeSlot.date;

        // Build start datetime
        const startDateTime = new Date(startDate);
        startDateTime.setHours(Math.floor(selectedTimeSlot.startHour || startHourInt), 0, 0, 0);

        // Build end datetime - default 2 hours from start if not specified
        const endDateTime = new Date(endDate);
        endDateTime.setHours(Math.floor(selectedTimeSlot.endHour || (selectedTimeSlot.startHour || startHourInt) + 2), 0, 0, 0);

        setFormData({
          maintenanceId: '',
          startDateTime,
          endDateTime,
          serviceBayId: selectedTimeSlot.resourceType === 'bay' ? selectedTimeSlot.resourceId : '',
          employeeIds: selectedTimeSlot.resourceType === 'employee' ? [selectedTimeSlot.resourceId] : [],
        });
      } else {
        // Default: today, work hours
        const startDateTime = new Date();
        setTimeOfDay(startDateTime, startHourInt);
        const endDateTime = new Date(startDateTime);
        setTimeOfDay(endDateTime, startHourInt + 2);

        setFormData({
          maintenanceId: '',
          startDateTime,
          endDateTime,
          serviceBayId: '',
          employeeIds: [],
        });
      }
    }
  }, [open, selectedTimeSlot, selectedEvent, isEditMode, startHourInt]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData((prev) => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(employeeId)
        ? prev.employeeIds.filter((id) => id !== employeeId)
        : [...prev.employeeIds, employeeId],
    }));
  };

  const handleSubmit = async () => {
    // A new customer with nothing to identify it by would be skipped below and its details lost: refuse
    if (!isEditMode && creationMode === 'new' && customerMode === 'new' && isNewCustomerIncomplete(newCustomer)) {
      setError(t('customer.newIdentityMissing'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDateTime = formData.startDateTime;
      const endDateTime = formData.endDateTime;

      if (isEditMode) {
        // Update existing schedule; employee_ids is this slot's own team
        await axios.patch(`/api/scheduling/schedules/${selectedEvent.id}/`, {
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
          ...(selectedEvent.has_active_booking ? { notify_customer: notifyCustomer } : {}),
        });

        // Persist customer reminder/notification choices (non-fatal on failure)
        await reminders.save(selectedEvent.id);
      } else if (creationMode === 'new') {
        // Create new customer first if needed
        let customerId = null;
        if (customerMode === 'existing') {
          customerId = newMaintenanceData.customerId || null;
        } else if (customerMode === 'new' && isNewCustomerValid(newCustomer)) {
          try {
            const customerResponse = await axios.post('/api/customers/', newCustomer);
            customerId = customerResponse.data.id;
          } catch (customerErr) {
            const errorMessage = customerErr.response?.data?.non_field_errors?.[0] ||
              customerErr.response?.data?.detail ||
              Object.values(customerErr.response?.data || {}).flat().join(', ') ||
              t('Failed to create customer');
            setError(errorMessage);
            setLoading(false);
            return;
          }
        }

        // Create new maintenance
        const maintenancePayload = {
          vehicle: newMaintenanceData.vehicle || {
            registration_number: newMaintenanceData.registrationNumber.toUpperCase(),
          },
          description: newMaintenanceData.description,
          state: 'planned',
        };

        if (customerId) {
          maintenancePayload.customer_id = customerId;
        }

        const maintenanceResponse = await axios.post('/api/maintenances/', maintenancePayload);
        const newMaintenanceId = maintenanceResponse.data.id;

        // Create schedule for the new maintenance; employee_ids is the slot's team
        const scheduleResponse = await axios.post('/api/scheduling/schedules/', {
          maintenance: newMaintenanceId,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
        });

        // Persist customer reminder/notification choices (non-fatal on failure)
        await reminders.save(scheduleResponse.data.id);
      } else {
        // Link existing maintenance (existing mode): a new slot with its own team. An
        // empty selection lets the backend copy the work order team onto the slot.
        const scheduleResponse = await axios.post('/api/scheduling/schedules/', {
          maintenance: formData.maintenanceId,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
        });

        // Persist customer reminder/notification choices (non-fatal on failure)
        await reminders.save(scheduleResponse.data.id);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving schedule:', err);
      const data = err.response?.data;
      let message;
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (data.detail) {
          message = data.detail;
        } else if (data.non_field_errors) {
          message = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(', ') : data.non_field_errors;
        } else {
          const fieldErrors = Object.entries(data)
            .map(([field, errors]) => {
              const errorMsg = Array.isArray(errors) ? errors.join(', ') : String(errors);
              return `${field}: ${errorMsg}`;
            })
            .join('; ');
          message = fieldErrors || t('Failed to save schedule');
        }
      } else {
        message = err.message || t('Failed to save schedule');
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    if (!selectedEvent) return;

    setLoading(true);
    setError(null);

    try {
      // Delete only the schedule - employee assignments should be preserved
      await axios.delete(`/api/scheduling/schedules/${selectedEvent.id}/`);

      onSuccess();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError(err.response?.data?.detail || t('Failed to delete schedule'));
    } finally {
      setLoading(false);
    }
  };

  const eventColors = selectedEvent
    ? resolveEvent(
        { ...selectedEvent, state: selectedEvent.state || selectedEvent.status, color_tag_id: colorTagId },
        false
      )
    : null;
  const config = eventColors ? eventColors.stateConfig : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEditMode ? t('Edit schedule') : t('Schedule maintenance')}
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {isEditMode && selectedEvent?.has_active_booking && (
          <BookingNotifyChoice
            checked={notifyCustomer}
            onChange={setNotifyCustomer}
            showChoice={
              !formData.startDateTime ||
              new Date(selectedEvent.start_time).getTime() !== formData.startDateTime.getTime()
            }
            sx={{ mb: 2 }}
          />
        )}
        {error && (
          <Alert ref={errorRef} severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Nothing to book? The tapped hour can take a calendar note or, in a mechanic's
            column, an absence instead: the phone has no drag menu, so the desktop's menu
            entries sit here, one tap past the slot, behind the same permission as the
            desktop's drag selection. */}
        {!isEditMode && canSchedule && (onAddNote || onAddUnavailability) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
            {onAddNote && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<StickyNoteIcon sx={{ color: '#0ea5e9' }} />}
                onClick={onAddNote}
                data-testid="mobile-schedule-add-note"
                sx={ALT_ACTION_SX}
              >
                {t('Add note to calendar')}
              </Button>
            )}
            {onAddUnavailability && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<BlockIcon sx={{ color: '#ec4899' }} />}
                onClick={onAddUnavailability}
                data-testid="mobile-schedule-add-unavailability"
                sx={ALT_ACTION_SX}
              >
                {t('Mark as unavailable')}
              </Button>
            )}
          </Box>
        )}

        {/* Maintenance Selection/Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarIcon fontSize="small" />
            {t('Maintenance')}
          </Typography>

          {isEditMode ? (
            <Box
              sx={{
                p: 2,
                bgcolor: eventColors?.bg || 'grey.50',
                borderRadius: 2,
                borderLeft: '4px solid',
                borderLeftColor: eventColors?.border || 'grey.400',
                ...(eventColors?.ringColor
                  ? { outline: `2px solid ${eventColors.ringColor}`, outlineOffset: '-2px' }
                  : null),
              }}
            >
              {/* Header row: Registration number and status */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    ...plateFont,
                    fontSize: '16px',
                  }}
                >
                  {selectedEvent?.vehicle_registration}
                </Typography>
                {(selectedEvent?.status || selectedEvent?.state) && (
                  <Chip
                    size="small"
                    label={t(selectedEvent.status || selectedEvent.state)}
                    sx={{
                      fontSize: '11px',
                      height: 22,
                      bgcolor: config?.bg || 'grey.100',
                      color: config?.text || 'text.secondary',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>

              {/* Work number */}
              {selectedEvent?.work_number && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <AssignmentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    #{selectedEvent.work_number}
                  </Typography>
                </Box>
              )}

              {/* Customer */}
              {selectedEvent?.customer_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {selectedEvent.customer_name}
                  </Typography>
                </Box>
              )}

              {/* Description */}
              {selectedEvent?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1.5 }}>
                  {selectedEvent.description}
                </Typography>
              )}

              {/* Colour tag (väritunniste) */}
              {canTag && activeTags.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    data-testid="mobile-schedule-color-tag-select"
                    value={colorTagId ?? ''}
                    onChange={(e) => handleColorTagChange(e.target.value)}
                    renderValue={(value) => {
                      const tag = activeTags.find((candidate) => candidate.id === value);
                      if (!tag) return t('calendar.colorTags.selectNone');
                      return (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: tag.color }} />
                          <span>{tag.name}</span>
                        </Box>
                      );
                    }}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <MenuItem value="">{t('calendar.colorTags.selectNone')}</MenuItem>
                    {activeTags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: tag.color, mr: 1 }} />
                        {tag.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {/* Navigate to maintenance button */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  onClose();
                  navigate(`/maintenances/${selectedEvent?.maintenance_id}`);
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  borderRadius: 2,
                  borderColor: config?.color || 'divider',
                  color: config?.text || 'text.primary',
                  '&:hover': {
                    borderColor: config?.color || 'divider',
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {t('View maintenance')}
              </Button>
            </Box>
          ) : (
            <>
              {/* Mode Toggle */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  icon={<AddIcon sx={{ fontSize: 16 }} />}
                  label={t('Create new')}
                  onClick={() => setCreationMode('new')}
                  variant={creationMode === 'new' ? 'filled' : 'outlined'}
                  color={creationMode === 'new' ? 'primary' : 'default'}
                  sx={{ flex: 1, justifyContent: 'center', fontSize: '12px' }}
                />
                <Chip
                  icon={<LinkIcon sx={{ fontSize: 16 }} />}
                  label={t('Link existing')}
                  onClick={() => setCreationMode('existing')}
                  variant={creationMode === 'existing' ? 'filled' : 'outlined'}
                  color={creationMode === 'existing' ? 'primary' : 'default'}
                  sx={{ flex: 1, justifyContent: 'center', fontSize: '12px' }}
                />
              </Box>

              {creationMode === 'new' ? (
                /* Create New Maintenance Form */
                <Box>
                  {/* Registration Number */}
                  <Autocomplete
                    freeSolo
                    open={vehicleTypeaheadOptions.length > 0 && !vehicleLookedUp}
                    options={vehicleTypeaheadOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option.registration_number || '')}
                    filterOptions={(x) => x}
                    loading={vehicleTypeaheadLoading}
                    onOpen={() => { vehicleTypeaheadOpenRef.current = true; }}
                    onClose={() => { vehicleTypeaheadOpenRef.current = false; }}
                    inputValue={newMaintenanceData.registrationNumber}
                    onInputChange={(event, newValue, reason) => {
                      if (reason === 'input') {
                        handleRegistrationChange(newValue);
                      } else if (reason === 'clear') {
                        handleRegistrationChange('');
                      }
                    }}
                    onChange={(event, newValue, reason) => {
                      if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
                        handleVehicleTypeaheadSelect(newValue);
                      }
                    }}
                    renderOption={(props, option) => {
                      const { key, ...restProps } = props;
                      return (
                        <Box component="li" key={option.id} {...restProps}>
                          <Box sx={{ width: '100%' }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, ...plateFont }}
                            >
                              {option.registration_number}
                              {(option.manufacturer || option.model) && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 1, fontFamily: 'inherit', fontWeight: 400 }}
                                >
                                  {[option.manufacturer, option.model].filter(Boolean).join(' ')}
                                  {option.year_model ? ` (${option.year_model})` : ''}
                                </Typography>
                              )}
                            </Typography>
                            {option.last_customer_name && (
                              <Typography variant="caption" color="text.secondary">
                                {option.last_customer_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoComplete="off"
                        placeholder="ABC-123"
                        fullWidth
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { ...plateFont, fontWeight: 600 },
                          endAdornment: (
                            <>
                              {lookupLoading && <CircularProgress size={20} />}
                              {vehicleLookedUp && !lookupLoading && (
                                <CheckCircleIcon sx={{ color: 'success.main' }} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                      />
                    )}
                    sx={{ mb: 1.5 }}
                  />

                  {/* Lookup error */}
                  {lookupError && (
                    <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                      {lookupError}
                    </Alert>
                  )}

                  {/* Trial Info Box - vehicle lookup is a paid feature */}
                  {showTrialInfo && (
                    <Alert
                      severity="info"
                      icon={<InfoOutlinedIcon />}
                      sx={{ mb: 1.5, py: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>
                        {t('Vehicle lookup is a paid feature')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('In the paid version, vehicle information is automatically fetched from the registry. During trial, you can enter vehicle details manually or proceed with just the registration number.')}
                      </Typography>
                    </Alert>
                  )}

                  {/* Vehicle info (shown after lookup) */}
                  <Collapse in={vehicleLookedUp && !!newMaintenanceData.vehicle?.manufacturer}>
                    <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 2, mb: 1.5, border: '1px solid', borderColor: 'success.200' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {newMaintenanceData.vehicle?.manufacturer} {newMaintenanceData.vehicle?.model}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {newMaintenanceData.vehicle?.year_model}
                        {newMaintenanceData.vehicle?.fuel_type && ` • ${newMaintenanceData.vehicle.fuel_type}`}
                        {newMaintenanceData.vehicle?.engine_kw && ` • ${newMaintenanceData.vehicle.engine_kw} kW`}
                      </Typography>
                    </Box>
                  </Collapse>

                  {/* Description */}
                  <TextField
                    placeholder={t('Description (optional)')}
                    value={newMaintenanceData.description}
                    onChange={(e) => setNewMaintenanceData((prev) => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                  />

                  {/* Customer Selection */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                      {t('Customer')} ({t('optional')})
                    </Typography>

                    {/* Customer Mode Toggle */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                      <Chip
                        icon={<PersonSearchIcon sx={{ fontSize: 16 }} />}
                        label={t('Existing customer')}
                        onClick={() => setCustomerMode('existing')}
                        variant={customerMode === 'existing' ? 'filled' : 'outlined'}
                        color={customerMode === 'existing' ? 'primary' : 'default'}
                        sx={{ flex: 1, justifyContent: 'center', fontSize: '12px' }}
                      />
                      <Chip
                        icon={<AddIcon sx={{ fontSize: 16 }} />}
                        label={t('New customer')}
                        onClick={() => setCustomerMode('new')}
                        variant={customerMode === 'new' ? 'filled' : 'outlined'}
                        color={customerMode === 'new' ? 'primary' : 'default'}
                        sx={{ flex: 1, justifyContent: 'center', fontSize: '12px' }}
                      />
                    </Box>

                    {/* Existing Customer Search */}
                    {customerMode === 'existing' && (
                      <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => customerLabel(option)}
                        value={customers.find((c) => c.id === newMaintenanceData.customerId) || null}
                        onChange={(e, newValue) => { setNewMaintenanceData((prev) => ({ ...prev, customerId: newValue?.id || '' })); customerAutoFilledRef.current = newValue ? 'manual' : 'none'; }}
                        filterOptions={(x) => x}
                        onInputChange={(e, newInputValue, reason) => {
                          if (reason === 'input') {
                            setCustomerSearchTerm(newInputValue);
                          }
                        }}
                        loading={customerLoading}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            autoComplete="off"
                            placeholder={t('Search by name, email, phone, or business ID...')}
                            size="small"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {customerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {option.type === 'company' ? (
                                  <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                ) : (
                                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                )}
                                <Typography variant="body2">{customerLabel(option)}</Typography>
                              </Box>
                              {option.email && (
                                <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                              )}
                            </Box>
                          </li>
                        )}
                        noOptionsText={customerSearchTerm ? t('No customers found') : t('Type to search...')}
                      />
                    )}

                    {/* Customer's existing vehicles - shown only before a registration is entered */}
                    {customerMode === 'existing' && newMaintenanceData.customerId && !newMaintenanceData.registrationNumber && (
                      <CustomerVehicleChips
                        customerId={newMaintenanceData.customerId}
                        onSelect={handleVehicleTypeaheadSelect}
                      />
                    )}

                    {/* New Customer Form */}
                    {customerMode === 'new' && (
                      <NewCustomerForm
                        t={t}
                        value={newCustomer}
                        onChange={setNewCustomer}
                        size="small"
                        fieldSx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                      />
                    )}
                  </Box>
                </Box>
              ) : (
                /* Link Existing Maintenance */
                <MaintenanceSearchAutocomplete
                  t={t}
                  value={formData.maintenanceId}
                  onChange={(id) => handleFieldChange('maintenanceId', id)}
                />
              )}
            </>
          )}
        </Box>

        {/* Start DateTime */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon fontSize="small" />
            {t('Start time')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DateTimePicker
              value={formData.startDateTime}
              onChange={(newValue) => handleFieldChange('startDateTime', newValue)}
              ampm={false}
              format="dd.MM.yyyy HH:mm"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* End DateTime */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('End time')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DateTimePicker
              value={formData.endDateTime}
              onChange={(newValue) => handleFieldChange('endDateTime', newValue)}
              ampm={false}
              format="dd.MM.yyyy HH:mm"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Quick Duration Buttons */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('Quick duration')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {DURATION_OPTIONS.slice(0, 6).map((opt) => {
              // Calculate if this duration matches current selection
              const isSelected = formData.startDateTime && formData.endDateTime &&
                Math.abs((formData.endDateTime - formData.startDateTime) / (1000 * 60 * 60) - opt.value) < 0.01;

              return (
                <Chip
                  key={opt.value}
                  label={t(opt.labelKey)}
                  onClick={() => {
                    if (formData.startDateTime) {
                      const newEndDateTime = new Date(formData.startDateTime);
                      newEndDateTime.setTime(newEndDateTime.getTime() + opt.value * 60 * 60 * 1000);
                      handleFieldChange('endDateTime', newEndDateTime);
                    }
                  }}
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  sx={{ fontSize: '13px' }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Service Bay */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon fontSize="small" />
            {t('Service bay')}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={formData.serviceBayId}
              onChange={(e) => handleFieldChange('serviceBayId', e.target.value)}
              displayEmpty
              sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
            >
              <MenuItem value="">
                <Typography color="text.secondary">{t('Not selected')}</Typography>
              </MenuItem>
              {serviceBays.map((bay) => (
                <MenuItem key={bay.id} value={bay.id}>
                  {bay.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Employees */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" />
            {t('Employees')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {employees.map((emp) => (
              <Chip
                key={emp.id}
                label={emp.name}
                onClick={() => handleEmployeeToggle(emp.id)}
                variant={formData.employeeIds.includes(emp.id) ? 'filled' : 'outlined'}
                color={formData.employeeIds.includes(emp.id) ? 'primary' : 'default'}
                avatar={
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: emp.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 600,
                    }}
                  >
                    {emp.initials}
                  </Box>
                }
                sx={{ fontSize: '13px' }}
              />
            ))}
          </Box>
          {formData.employeeIds.length === 0 && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {t('Select at least one employee')}
            </Typography>
          )}
        </Box>

        {/* Customer reminder / notification */}
        <ScheduleReminderSection
          {...reminders}
          disabled={loading}
          contactOverride={
            !isEditMode && creationMode === 'new'
              ? customerMode === 'new'
                ? { phone: newCustomer.phone, email: newCustomer.email }
                : (() => {
                    const selected = customers.find((c) => c.id === newMaintenanceData.customerId);
                    // No customer picked yet — a missing-contact warning would mislead
                    return selected ? { phone: selected.phone || '', email: selected.email || '' } : null;
                  })()
              : null
          }
        />

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={
              loading ||
              !canSchedule ||
              formData.employeeIds.length === 0 ||
              (!isEditMode && creationMode === 'existing' && !formData.maintenanceId) ||
              (!isEditMode && creationMode === 'new' && !newMaintenanceData.registrationNumber)
            }
            sx={{
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {loading ? <CircularProgress size={24} /> : <span>{t('Save')}</span>}
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={onClose}
            sx={{
              borderRadius: 2,
              py: 1.5,
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {t('Cancel')}
          </Button>

          {isEditMode && (
            <Button
              variant="text"
              size="large"
              color="error"
              onClick={handleDelete}
              disabled={loading || !canSchedule}
              startIcon={<DeleteIcon />}
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontWeight: 500,
                textTransform: 'none',
                mt: 2,
              }}
            >
              {deleteConfirm ? <span>{t('Tap again to confirm')}</span> : <span>{t('Delete reservation')}</span>}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default withTranslation()(MobileScheduleDialog);
